import { useCallback, useMemo } from 'react';
import { Channel, FlightRoomUserStatus } from '@connect-x-app/shared-domain-models';

import {
  getUserByExternalId,
  selectRoomChannels,
  selectSelectedChannelId,
  useAuthenticationStore,
  useChannelSessionStore,
  useChannelStore,
  useFlightRoomSessionStore,
  useFlightRoomStore,
} from '../../stores';
import { useChatChannelSocketSendEvents, useChatMessageSocketSendEvents } from '../socketEvents';

export const useChannels = () => {
  const { user } = useAuthenticationStore();
  const { selectedFlightRoomId } = useFlightRoomSessionStore();
  const {
    addUserToChannel,
    channelsMap,
    removeUserFromChannel,
    setChannels,
    updatePartialCustomChannel,
    updatePartialMainChannel,
  } = useChannelStore();
  const { updatePartialFlightRoom } = useFlightRoomStore();
  const { selectedChannelId, setSelectedChannelId } = useChannelSessionStore();
  const { sendChatJoinChannel, sendChatLeaveChannel } = useChatChannelSocketSendEvents();
  const { sendChatReadMessage } = useChatMessageSocketSendEvents();

  const roomChannels: { customChannels: Channel[]; mainChannel: Channel } | null = useMemo(() => {
    return selectedFlightRoomId ? (channelsMap.get(selectedFlightRoomId) ?? null) : null;
  }, [channelsMap, selectedFlightRoomId]);

  const selectedChannel = useMemo(() => {
    if (roomChannels && selectedChannelId) {
      if (roomChannels.mainChannel.id === selectedChannelId) {
        return roomChannels.mainChannel;
      } else {
        return roomChannels.customChannels.find(channel => channel.id === selectedChannelId) ?? null;
      }
    }
    return null;
  }, [selectedChannelId, roomChannels]);

  const clearFirstUnreadMessage = useCallback(
    (channelId: string) => {
      if (selectedFlightRoomId) {
        const partialChannel = { firstUnreadMessage: null };
        if (roomChannels?.mainChannel.id === channelId) {
          updatePartialMainChannel(selectedFlightRoomId, partialChannel);
        } else {
          updatePartialCustomChannel(selectedFlightRoomId, channelId, partialChannel);
        }
      }
    },
    [roomChannels?.mainChannel.id, selectedFlightRoomId, updatePartialCustomChannel, updatePartialMainChannel],
  );

  const openChannel = useCallback(
    (roomId: string, channelId: string) => {
      setSelectedChannelId(channelId);

      const room = selectRoomChannels(roomId);
      if (!room) {
        return;
      }

      const isMain = room.mainChannel.id === channelId;
      const channel = isMain ? room.mainChannel : room.customChannels.find(c => c.id === channelId);
      if (!channel) {
        return;
      }

      const isUserInRoom = !!user && channel.users.some(u => u.externalId === user.externalId);
      if (!isUserInRoom) {
        return;
      }

      const wipedChannel = { hasUnreadMentions: false, unreadMessagesCount: 0 };
      if (isMain) {
        updatePartialMainChannel(roomId, wipedChannel);
      } else {
        updatePartialCustomChannel(roomId, channelId, wipedChannel);
      }

      const remainingChannels = isMain
        ? room.customChannels
        : [room.mainChannel, ...room.customChannels.filter(c => c.id !== channelId)];
      const totalUnread = remainingChannels.reduce(
        (sum, c) => (c.users.some(u => u.externalId === user.externalId) ? sum + (c.unreadMessagesCount ?? 0) : sum),
        0,
      );
      const anyMentions = remainingChannels.some(c => c.hasUnreadMentions);

      updatePartialFlightRoom(roomId, {
        hasUnreadMentions: anyMentions,
        unreadMessagesCount: totalUnread,
      });
    },
    [setSelectedChannelId, updatePartialCustomChannel, updatePartialFlightRoom, updatePartialMainChannel, user],
  );

  const closeChannel = useCallback(
    (roomId: string, channelId: string) => {
      if (selectSelectedChannelId() === channelId) {
        setSelectedChannelId(null);
      }

      const room = selectRoomChannels(roomId);
      if (!room) {
        return;
      }
      const partialChannel = { firstUnreadMessage: null };
      if (room.mainChannel.id === channelId) {
        updatePartialMainChannel(roomId, partialChannel);
      } else {
        updatePartialCustomChannel(roomId, channelId, partialChannel);
      }
    },
    [setSelectedChannelId, updatePartialCustomChannel, updatePartialMainChannel],
  );

  const joinChannel = useCallback(
    async (chatroomId: string, channelId: string) => {
      const ack = await sendChatJoinChannel(chatroomId, channelId);
      if (ack?.ok && user) {
        const channelUser = getUserByExternalId(user.externalId);
        if (channelUser) {
          addUserToChannel(chatroomId, channelId, { ...channelUser, status: FlightRoomUserStatus.JOINED });
        }
      }
      return ack;
    },
    [addUserToChannel, sendChatJoinChannel, user],
  );

  const leaveChannel = useCallback(
    async (chatroomId: string, channelId: string) => {
      const ack = await sendChatLeaveChannel(chatroomId, channelId);
      if (ack?.ok) {
        if (user) {
          removeUserFromChannel(chatroomId, channelId, user.externalId);
        }
        setSelectedChannelId(null);
      }
      return ack;
    },
    [removeUserFromChannel, sendChatLeaveChannel, setSelectedChannelId, user],
  );

  return {
    clearFirstUnreadMessage,
    closeChannel,
    joinChannel,
    leaveChannel,
    openChannel,
    roomChannels,
    selectedChannel,
    selectedChannelId,
    sendChatReadMessage,
    setChannels,
  };
};
